/** Initial code inspired from https://github.com/cytoscape/cytoscape.js-automove - MIT License
    Modified to suit custom requirements of vCloud Air Datacenter Designer
 */

let jQuery = require('jquery');
let cytoscape = require('cytoscape');

export function AddAutoMove() {

    let defaults = {
        // specify nodes that should be automoved with one of
        // - a function that returns true for matching nodes
        // - a selector that matches the nodes
        nodesMatching: function (node) {
            return false;
        },
        // specify how a node's position should be updated with one of
        // - function( node ){ return { x: 1, y: 2 }; } => put the node where the function returns
        // - { x1, y1, x2, y2 } => constrain the node position within the bounding box (in model co-ordinates)
        // - 'mean' => put the node in the average position of its neighbourhood
        // - 'viewport' => keeps the node body within the viewport
        reposition: 'mean',
        // specify when the repositioning should occur by specifying a function that
        // calls update() when reposition updates should occur
        // - function( update ){ /* ... */ } => a manual function for updating
        // - 'matching' => automatically update on position events for nodesMatching
        // - set efficiently and automatically for
        //   - reposition: 'mean'
        //   - reposition: { x1, y1, x2, y2 }
        //   - reposition: 'viewport'
        // - default/undefined => on a position event for any node (not as efficient...)
        when: undefined
    };

    let typeofStr = typeof '';
    let typeofObj = typeof {};

    let isObject = function (x) {
        return typeof x === typeofObj;
    };

    let isString = function (x) {
        return typeof x === typeofStr;
    };

    let assign = Object.assign ? Object.assign.bind(Object) : function (target, a, b) {
        let args = arguments;
        for (let i = 1; i < args.length; i++) {
            let obj = args[i];
            for (let k in obj) {
                target[k] = obj[k];
            }
        }
        return target;
    };

    let requestAnimationFrame = ( window.requestAnimationFrame || window.webkitRequestAnimationFrame ).bind(window);

    let onNextTick = function (fn) {
        setTimeout(fn, 0);
    };

    let eleMatchesSpec = function (ele, spec) {
        if (isString(spec)) {
            return ele.is(spec);
        } else {
            return spec(ele);
        }
    };

    let bindings = [];

    let bind = function (cy, events, selector, fn) {
        cy.on(events, 'node', fn);
        let b = {cy: cy, events: events, selector: 'node', fn: fn};
        bindings.push(b);
        return b;
    };

    let bindOnRule = function (rule, cy, events, selector, fn) {
        let b = bind(cy, events, selector, fn);
        let bindings = rule.bindings = rule.bindings || [];
        bindings.push(b);
    };

    let unbindAll = function (cy) {
        let sameCy = function (b) {
            return cy === b.cy;
        };
        let unbind = function (b) {
            b.cy.off(b.events, b.selector, b.fn);
        };
        bindings.filter(sameCy).forEach(unbind);
        bindings = [];
    };

    let unbindAllOnRule = function (rule) {
        let unbind = function (b) {
            b.cy.off(b.events, b.selector, b.fn);
        };
        rule.bindings.forEach(unbind);
        rule.bindings = [];
    };

    let getRepositioner = function (spec, cy) {
        if (spec === 'mean') {
            return meanNeighborhoodPosition;
        } else if (spec === 'viewport') {
            return viewportPosition(cy);
        } else if (isObject(spec)) {
            return boxPosition(spec);
        } else {
            return spec;
        }
    };

    let meanNeighborhoodPosition = function (node) {
        let nhood = node.neighbourhood().nodes();
        let avgPos = {x: 0, y: 0};
        for (let i = 0; i < nhood.length; i++) {
            let pos = nhood[i].position();

            avgPos.x += pos.x;
            avgPos.y += pos.y;
        }
        avgPos.x /= nhood.length;
        avgPos.y /= nhood.length;

        return avgPos;
    };

    let constrain = function (val, min, max) {
        return val < min ? min : ( val > max ? max : val );
    };

    let constrainInBox = function (node, bb) {
        let pos = node.position();
        return {
            x: constrain(pos.x, bb.x1, bb.x2),
            y: constrain(pos.y, bb.y1, bb.y2)
        };
    };

    let boxPosition = function (bb) {
        return function (node) {
            return constrainInBox(node, bb);
        };
    };

    let viewportPosition = function (cy) {
        return function (node) {
            let extent = cy.extent();
            let w = node.outerWidth();
            let h = node.outerHeight();
            let bb = {
                x1: extent.x1 + w / 2,
                x2: extent.x2 - w / 2,
                y1: extent.y1 + h / 2,
                y2: extent.y2 - h / 2
            };
            return constrainInBox(node, bb);
        };
    };

    let meanListener = function (rule) {
        return function (update, cy) {
            bindOnRule(rule, cy, 'position', 'node', function () {
                let movedNode = this;
                if (movedNode.closedNeighborhood().some(rule.matches)) {
                    update(cy, [rule]);
                }
            });
        };
    };

    let matchingNodesListener = function (rule) {
        return function (update, cy) {
            bindOnRule(rule, cy, 'position', 'node', function () {
                let movedNode = this;
                if (rule.matches(movedNode)) {
                    update(cy, [rule]);
                }
            });
        };
    };

    let getListener = function (cy, rule) {
        if (rule.reposition === 'mean') {
            return meanListener(rule);
        } else if ( isObject(rule.reposition) || rule.when === 'matching' || rule.reposition === 'viewport') {
            return matchingNodesListener(rule);
        } else {
            return rule.when;
        }
    };

    let addRule = function (cy, scratch, options) {
        let rule = assign({}, defaults, options);
        rule.getNewPos = getRepositioner(rule.reposition, cy);
        rule.matches = function (ele) {
            return eleMatchesSpec(ele, rule.nodesMatching);
        };
        rule.listener = getListener(cy, rule);
        rule.listener(function () {
            update(cy, [rule]);
        }, cy);
        rule.enabled = true;
        scratch.rules.push(rule);
        return rule;
    };

    let update = function (cy, rules) {
        let scratch = cy.scratch().automove;
        let nodes = cy.nodes();
        rules = rules != null ? rules : scratch.rules;
        cy.batch(function () { // batch for performance
            for (let i = 0; i < nodes.length; i++) {
                let node = nodes[i];

                for (let j = 0; j < rules.length; j++) {
                    let rule = rules[j];

                    if (rule.destroyed || !rule.enabled) {
                        break;
                    } // ignore destroyed rules b/c user may use custom when()
                    if (!rule.matches(node)) {
                        continue;
                    }
                    let pos = node.position();
                    let newPos = rule.getNewPos(node);
                    let newPosIsDiff = pos.x !== newPos.x || pos.y !== newPos.y;
                    if (newPosIsDiff) { // only update on diff for perf
                        node.position(newPos);
                    }
                }
            }
        });
    };

    let register = function (cytoscape) {
        if (!cytoscape) {
            return;
        }
        cytoscape('core', 'AutoMove', function (options) {
            let cy = this;
            let scratch = cy.scratch().automove = cy.scratch().automove || {
                    rules: []
                };
            if (options === 'destroy') {
                scratch.rules.forEach(function (r) {
                    r.destroy();
                });
                scratch.rules.splice(0, scratch.rules.length);
                return;
            }
            let rule = addRule(cy, scratch, options);
            update(cy, [rule]); // do an initial update to make sure the start state is correct
            return {
                disable: function () {
                    this.toggle(false);
                },
                enable: function () {
                    this.toggle(true);
                },
                enabled: function () {
                    return rule.enabled;
                },
                toggle: function (on) {
                    rule.enabled = on !== undefined ? on : !rule.enabled;
                    if (rule.enabled) {
                        update(cy, [rule]);
                    }
                },
                destroy: function () {
                    let rules = scratch.rules;
                    unbindAllOnRule(rule);
                    rules.splice(rules.indexOf(rule), 1);
                    return this;
                }
            };
        });
    };

    if (typeof cytoscape !== 'undefined') {
        register(cytoscape);
    }
};



// WEBPACK FOOTER //
// ./src/common/vca-graph/vca-graph-automove.cytoscape.ts